import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  DataGrid,
  Dialog,
  FilterBar,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Skeleton,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Eye as ViewIcon,
  Hammer as LienIcon,
  AlertTriangle as WarningIcon,
  AlertCircle as ErrorIcon,
  Plus as PlusIcon,
} from 'lucide-react';
import {
  useLienVerifications,
  useCreateLienVerification,
} from '../hooks';
import type { LienVerification } from '../hooks';
import { useTenantNames } from '../hooks/useTenantNames';
import type { TenantNameState } from '../hooks/useTenantNames';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
} from '../utils/display-format';
import {
  CollateralListEmptyState,
  LIEN_DATA_ABSENT,
  LIEN_DATA_REFUSAL_REASON,
  LIEN_RISK_LEVEL_LABEL,
  LienRiskBadge,
  TAX_LIEN_NOT_ASSESSED,
  TAX_LIEN_RISK_LABEL,
  TaxLienRiskBadge,
  TenantLoanCell,
  formatLienCount,
  isMeasured,
  labelOptions,
  tenantDisplayName,
} from '../lib/collateral-display';
import type { CollateralEmptyCopyConfig } from '../lib/collateral-display';

/** The spec's maximum page. The counts below are taken over the rows read. */
const LIST_LIMIT = 100;

export const LIEN_VERIFICATION_EMPTY_COPY: CollateralEmptyCopyConfig = {
  failedTitle: 'Lien verifications could not be read',
  filteredTitle: 'No lien verifications match the filters',
  emptyTitle: 'No lien verifications recorded',
  emptyReason: LIEN_DATA_REFUSAL_REASON,
};

/** The filter value that selects verifications with no tax lien assessment. */
export const TAX_LIEN_NOT_ASSESSED_VALUE = 'NOT_ASSESSED';

/**
 * The tax lien filter and the Clear / Review required cards read ONE map,
 * keyed by the TaxLienRisk enum (property-title-admin.openapi.yaml, op-32). The
 * tabs this replaced filtered on CLEAR / ACCEPTABLE / REVIEW_REQUIRED /
 * UNACCEPTABLE, values the field never carries, so no tab could match a row
 * while the cards counted NONE and ELEVATED (op-g2-6).
 */
export const LIEN_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'taxLienRisk',
    label: 'Tax lien risk',
    type: 'select',
    options: [
      ...labelOptions(TAX_LIEN_RISK_LABEL),
      { value: TAX_LIEN_NOT_ASSESSED_VALUE, label: TAX_LIEN_NOT_ASSESSED },
    ],
  },
  {
    id: 'riskLevel',
    label: 'Risk level',
    type: 'select',
    options: labelOptions(LIEN_RISK_LEVEL_LABEL),
  },
];

export interface LienSummaryState {
  rows: number;
  taxLienClear: number;
  taxLienReviewRequired: number;
  criticalRisk: number;
  /** Sum of lienCount over verifications that carry one. */
  totalLiens: number;
  /** Verifications the sum could not include because they carry no count. */
  withoutLienCount: number;
}

export function summarizeLienVerifications(verifications: LienVerification[]): LienSummaryState {
  let totalLiens = 0;
  let withoutLienCount = 0;
  for (const v of verifications) {
    if (isMeasured(v.lienCount)) totalLiens += v.lienCount;
    else withoutLienCount += 1;
  }
  return {
    rows: verifications.length,
    taxLienClear: verifications.filter((v) => v.taxLienRisk === 'NONE').length,
    taxLienReviewRequired: verifications.filter((v) => v.taxLienRisk === 'ELEVATED').length,
    criticalRisk: verifications.filter((v) => v.riskLevel === 'CRITICAL').length,
    totalLiens,
    withoutLienCount,
  };
}

/** Whether a verification passes the tax lien filter value. */
export function matchesTaxLienFilter(verification: LienVerification, value: string | undefined): boolean {
  if (!value) return true;
  if (value === TAX_LIEN_NOT_ASSESSED_VALUE) return !verification.taxLienRisk;
  return verification.taxLienRisk === value;
}

/** A lien amount, or {@link LIEN_DATA_ABSENT} when the record carries none. */
function formatLienAmount(value: number | null | undefined): string {
  return isMeasured(value) ? formatMoney(value) : LIEN_DATA_ABSENT;
}

function formatPosition(value: number | null | undefined): string {
  return isMeasured(value) ? `#${value}` : ABSENT;
}

function addressLine(verification: LienVerification): string {
  const a = verification.address;
  return [a?.city, [a?.state, a?.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

function useColumns(
  names: ReadonlyMap<string, TenantNameState>,
  onView: (verification: LienVerification) => void,
): ColumnDef<LienVerification, unknown>[] {
  return useMemo<ColumnDef<LienVerification, unknown>[]>(
    () => [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (row) => row.address?.street ?? '',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {row.original.address?.street ?? ABSENT}
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal">
              {addressLine(row.original)}
            </div>
          </div>
        ),
        size: 220,
      },
      {
        id: 'liens',
        header: 'Liens',
        accessorFn: (row) => (isMeasured(row.lienCount) ? row.lienCount : -1),
        cell: ({ row }) => (
          <span className={`text-sm font-sans ${isMeasured(row.original.lienCount) ? 'text-primary' : 'text-tertiary'}`}>
            {formatLienCount(row.original.lienCount)}
          </span>
        ),
        size: 120,
      },
      {
        id: 'totalLienAmount',
        header: 'Total Amount',
        accessorFn: (row) => (isMeasured(row.totalLienAmount) ? row.totalLienAmount : -1),
        cell: ({ row }) => (
          <span
            className={`text-sm tabular-nums ${isMeasured(row.original.totalLienAmount) ? 'font-mono text-primary' : 'font-sans text-tertiary'}`}
          >
            {formatLienAmount(row.original.totalLienAmount)}
          </span>
        ),
        size: 150,
      },
      {
        id: 'position',
        header: 'Position',
        accessorFn: (row) => row.recommendedPosition ?? -1,
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatPosition(row.original.recommendedPosition)}
          </span>
        ),
        size: 90,
      },
      {
        id: 'riskLevel',
        header: 'Risk',
        accessorFn: (row) => row.riskLevel ?? '',
        cell: ({ row }) => <LienRiskBadge level={row.original.riskLevel} />,
        size: 110,
      },
      {
        id: 'taxLienRisk',
        header: 'Tax Lien Status',
        accessorFn: (row) => row.taxLienRisk ?? '',
        cell: ({ row }) => <TaxLienRiskBadge risk={row.original.taxLienRisk} />,
        size: 140,
      },
      {
        id: 'tenant',
        header: 'Tenant',
        accessorFn: (row) => tenantDisplayName(row.tenantId, names),
        cell: ({ row }) => (
          <TenantLoanCell tenantId={row.original.tenantId} loanId={undefined} names={names} />
        ),
        size: 170,
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string | undefined)}
          </span>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView(row.original);
            }}
            className="p-1 hover:bg-recessed rounded transition-colors"
            aria-label={`View lien verification details for ${row.original.address?.street ?? row.original.id}`}
            title="View details"
          >
            <ViewIcon size={20} className="text-secondary" />
          </button>
        ),
        size: 80,
      },
    ],
    [names, onView],
  );
}

interface AddressFormState {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

const EMPTY_ADDRESS: AddressFormState = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
};

const INPUT_CLASS =
  'w-full border border-recessed rounded px-3 py-2 text-sm bg-transparent outline-none focus:border-accent';

export function LienVerificationPage() {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newVerificationDialogOpen, setNewVerificationDialogOpen] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState<LienVerification | null>(null);
  const [addressForm, setAddressForm] = useState<AddressFormState>(EMPTY_ADDRESS);
  const [formError, setFormError] = useState<string | null>(null);

  // One unfiltered read. A server-side risk filter made the Critical Risk card
  // print a measured 0 whenever another risk level was selected.
  const {
    data: verificationData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useLienVerifications({ limit: LIST_LIMIT });

  const createMutation = useCreateLienVerification();

  const verifications = useMemo(() => verificationData?.data ?? [], [verificationData]);
  const tenantNames = useTenantNames(verifications.map((v) => v.tenantId));
  const summary = useMemo(() => summarizeLienVerifications(verifications), [verifications]);
  const total = verificationData?.total ?? verifications.length;
  const partialRead = total > verifications.length;

  const taxLienFilter = activeFilters.find((f) => f.filterId === 'taxLienRisk')?.value;
  const riskLevelFilter = activeFilters.find((f) => f.filterId === 'riskLevel')?.value;

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return verifications.filter((v) => {
      if (!matchesTaxLienFilter(v, taxLienFilter)) return false;
      if (riskLevelFilter && v.riskLevel !== riskLevelFilter) return false;
      if (!query) return true;
      const haystack = [
        v.address?.street,
        v.address?.city,
        v.address?.state,
        v.address?.zipCode,
        v.tenantId ? tenantDisplayName(v.tenantId, tenantNames) : undefined,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [verifications, taxLienFilter, riskLevelFilter, searchQuery, tenantNames]);

  const handleView = useCallback((verification: LienVerification) => {
    setSelectedVerification(verification);
  }, []);
  const columns = useColumns(tenantNames, handleView);

  function handleAddressFormChange(field: keyof AddressFormState, value: string) {
    setAddressForm((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  }

  async function handleCreateVerification() {
    const { street, city, state, zipCode } = addressForm;
    if (!street.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
      setFormError('All address fields are required.');
      return;
    }

    try {
      await createMutation.mutateAsync({
        address: {
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
        },
      });
      setAddressForm(EMPTY_ADDRESS);
      setNewVerificationDialogOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification request failed.';
      setFormError(message);
    }
  }

  const countNote = partialRead
    ? `Of the ${formatCount(verifications.length)} most recent of ${formatCount(total)}`
    : undefined;

  const totalLiensNote =
    summary.withoutLienCount > 0
      ? `Excludes ${formatCount(summary.withoutLienCount)} without lien data`
      : countNote;

  return (
    <div>
      <PageHeader
        description="Lien position analysis from ATTOM records, through the Reference Model"
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshIcon}
              iconPosition="left"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={LienIcon}
              iconPosition="left"
              onClick={() => {
                setAddressForm(EMPTY_ADDRESS);
                setFormError(null);
                setNewVerificationDialogOpen(true);
              }}
            >
              New Verification
            </Button>
          </>
        }
      />

      {error && (
        <Alert type="error" title="Lien verifications could not be read" className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span>{error instanceof Error ? error.message : 'An unexpected error occurred.'}</span>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))
        ) : error ? (
          <>
            <MetricCard label="Total Verifications" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label={`Tax Lien ${TAX_LIEN_RISK_LABEL.NONE}`} value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label={`Tax Lien ${TAX_LIEN_RISK_LABEL.ELEVATED}`} value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Critical Risk" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Total Liens" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard label="Total Verifications" value={formatCount(total)} />
            <MetricCard
              label={`Tax Lien ${TAX_LIEN_RISK_LABEL.NONE}`}
              value={formatCount(summary.taxLienClear)}
              description={countNote}
            />
            <MetricCard
              label={`Tax Lien ${TAX_LIEN_RISK_LABEL.ELEVATED}`}
              value={formatCount(summary.taxLienReviewRequired)}
              description={countNote}
            />
            <MetricCard
              label="Critical Risk"
              value={formatCount(summary.criticalRisk)}
              description={countNote}
            />
            <MetricCard
              label="Total Liens"
              value={formatCount(summary.totalLiens)}
              description={totalLiensNote}
            />
          </>
        )}
      </div>

      <FilterBar
        filters={LIEN_FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by address or tenant..."
        className="rounded-t-sm"
      />

      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<LienVerification>
          data={filtered}
          columns={columns}
          enablePagination
          defaultPageSize={25}
          hoverable
          getRowId={(row) => row.id}
          emptyState={
            <CollateralListEmptyState
              copy={LIEN_VERIFICATION_EMPTY_COPY}
              readFailed={!!error}
              filteredOut={!error && verifications.length > 0}
            />
          }
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {/* New Verification Dialog */}
      <Dialog
        open={newVerificationDialogOpen}
        onOpenChange={(open) => {
          if (!open) setNewVerificationDialogOpen(false);
        }}
        title="New Lien Verification"
        size="md"
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={() => setNewVerificationDialogOpen(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={PlusIcon}
              iconPosition="left"
              onClick={handleCreateVerification}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Submitting...' : 'Submit Verification'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <BodyText variant="ui" className="text-sm text-tertiary">
            Enter the property address to initiate a lien position analysis.
          </BodyText>

          {formError && <Alert type="error">{formError}</Alert>}

          <div>
            <label htmlFor="lien-street" className="block text-xs text-tertiary mb-1">Street Address</label>
            <input
              id="lien-street"
              type="text"
              value={addressForm.street}
              onChange={(e) => handleAddressFormChange('street', e.target.value)}
              placeholder="123 Main Street"
              className={INPUT_CLASS}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="lien-city" className="block text-xs text-tertiary mb-1">City</label>
              <input
                id="lien-city"
                type="text"
                value={addressForm.city}
                onChange={(e) => handleAddressFormChange('city', e.target.value)}
                placeholder="San Francisco"
                className={INPUT_CLASS}
                required
              />
            </div>
            <div>
              <label htmlFor="lien-state" className="block text-xs text-tertiary mb-1">State</label>
              <input
                id="lien-state"
                type="text"
                value={addressForm.state}
                onChange={(e) => handleAddressFormChange('state', e.target.value.toUpperCase())}
                placeholder="CA"
                maxLength={2}
                className={INPUT_CLASS}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="lien-zip" className="block text-xs text-tertiary mb-1">ZIP Code</label>
            <input
              id="lien-zip"
              type="text"
              inputMode="numeric"
              value={addressForm.zipCode}
              onChange={(e) => handleAddressFormChange('zipCode', e.target.value)}
              placeholder="94105"
              maxLength={10}
              className={INPUT_CLASS}
              required
            />
          </div>
        </div>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedVerification}
        onOpenChange={(open) => {
          if (!open) setSelectedVerification(null);
        }}
        title="Lien Verification Details"
        size="lg"
        footer={
          <Button variant="tertiary" onClick={() => setSelectedVerification(null)}>
            Close
          </Button>
        }
      >
        {selectedVerification && (
          <>
            {(selectedVerification.riskLevel === 'CRITICAL' ||
              selectedVerification.riskLevel === 'HIGH') && (
              <Card className="p-4 mb-4 bg-error/10 border border-error/20">
                <div className="flex items-start gap-2">
                  <ErrorIcon className="text-error flex-shrink-0" size={20} />
                  <div>
                    <BodyText variant="ui" className="text-error font-medium">
                      {selectedVerification.riskLevel === 'CRITICAL'
                        ? 'Critical Risk Profile'
                        : 'High Risk Profile'}
                    </BodyText>
                    <BodyText variant="ui" className="text-error/80 text-sm">
                      This property has a high lien burden that requires review before
                      proceeding with a lending decision.
                    </BodyText>
                  </div>
                </div>
              </Card>
            )}

            {selectedVerification.taxLienRisk === 'ELEVATED' && (
              <Card className="p-4 mb-4 bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <WarningIcon className="text-warning flex-shrink-0" size={20} />
                  <BodyText variant="ui" className="text-warning">
                    Elevated tax lien risk detected. Manual review is recommended before
                    proceeding.
                  </BodyText>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Property Address
                </BodyText>
                <BodyText variant="ui" className="font-medium">
                  {[selectedVerification.address?.street, addressLine(selectedVerification)].filter(Boolean).join(', ')}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Lien Count
                </BodyText>
                <BodyText variant="ui" className="font-medium text-lg">
                  {isMeasured(selectedVerification.lienCount) ? selectedVerification.lienCount : LIEN_DATA_ABSENT}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Total Lien Amount
                </BodyText>
                <BodyText variant="ui" className="font-medium text-lg">
                  {formatLienAmount(selectedVerification.totalLienAmount)}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Recommended Position
                </BodyText>
                <BodyText variant="ui" className="font-medium text-lg">
                  {formatPosition(selectedVerification.recommendedPosition)}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Risk Level
                </BodyText>
                <LienRiskBadge level={selectedVerification.riskLevel} />
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Tax Lien Risk
                </BodyText>
                <TaxLienRiskBadge risk={selectedVerification.taxLienRisk} />
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Data Source
                </BodyText>
                <BodyText variant="ui">
                  {selectedVerification.dataSource ?? ABSENT}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Tenant
                </BodyText>
                <TenantLoanCell
                  tenantId={selectedVerification.tenantId}
                  loanId={undefined}
                  names={tenantNames}
                />
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Verification ID
                </BodyText>
                <BodyText variant="ui" className="font-mono text-sm">{selectedVerification.id}</BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">
                  Created
                </BodyText>
                <BodyText variant="ui">
                  {formatDateTime(selectedVerification.createdAt)}
                </BodyText>
              </div>
            </div>

            {selectedVerification.liens && selectedVerification.liens.length > 0 && (
              <div className="mt-4">
                <BodyText variant="ui" className="text-xs text-tertiary mb-2">
                  Individual Liens ({selectedVerification.liens.length})
                </BodyText>
                <div className="space-y-2">
                  {selectedVerification.liens.map((lien, index) => (
                    <Card key={index} className="p-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                        <div>
                          <BodyText variant="ui" className="text-xs text-tertiary">
                            Lender
                          </BodyText>
                          <BodyText variant="ui">{lien.lenderName ?? ABSENT}</BodyText>
                        </div>
                        <div>
                          <BodyText variant="ui" className="text-xs text-tertiary">
                            Amount
                          </BodyText>
                          <BodyText variant="ui">{formatLienAmount(lien.amount)}</BodyText>
                        </div>
                        <div>
                          <BodyText variant="ui" className="text-xs text-tertiary">
                            Position
                          </BodyText>
                          <BodyText variant="ui">{formatPosition(lien.position)}</BodyText>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
}
